import { Box } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { type Control, type FieldErrors, type FieldValues } from "react-hook-form";

import { type JsonSchema } from "../types";
import { BooleanField } from "./SchemaFields/BooleanField";
import { DateField } from "./SchemaFields/DateField";
import { EnumField } from "./SchemaFields/EnumField";
import { NumberField } from "./SchemaFields/NumberField";
import { StringField } from "./SchemaFields/StringField";
import {
  isBooleanProperty,
  isDateProperty,
  isEnumProperty,
  isNumberProperty,
  isObjectProperty,
  type SchemaFieldProps,
} from "./SchemaFields/utils";

interface DynamicSchemaFormProps {
  schema: JsonSchema;
  pathPrefix: string;
  control: Control<FieldValues>;
  errors: FieldErrors<FieldValues>;
}

export function DynamicSchemaForm({ schema, pathPrefix, control, errors }: DynamicSchemaFormProps) {
  const { properties } = schema;

  if (!properties) {
    return null;
  }

  const requiredFields = schema.required ?? [];

  return (
    <>
      {Object.entries(properties).map(([key, property]) => {
        const fieldPath = pathPrefix ? `${pathPrefix}.${key}` : `properties.${key}`;
        const isRequired = requiredFields.includes(key);
        const fieldProps: SchemaFieldProps = {
          fieldKey: key,
          property,
          fieldPath,
          isRequired,
          control,
          errors,
        };

        if (isObjectProperty(property)) {
          return (
            <Box
              key={key}
              sx={(theme) => ({
                borderLeft: "2px solid",
                borderColor: theme.palette.divider,
                paddingLeft: 2,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              })}
            >
              <Text semibold variant="body2">
                {key}
              </Text>
              <DynamicSchemaForm
                schema={property}
                pathPrefix={fieldPath}
                control={control}
                errors={errors}
              />
            </Box>
          );
        }

        if (isEnumProperty(property)) {
          return <EnumField key={key} {...fieldProps} />;
        }

        if (isDateProperty(property)) {
          return <DateField key={key} {...fieldProps} />;
        }

        if (isNumberProperty(property)) {
          return <NumberField key={key} {...fieldProps} />;
        }

        if (isBooleanProperty(property)) {
          return <BooleanField key={key} {...fieldProps} />;
        }

        return <StringField key={key} {...fieldProps} />;
      })}
    </>
  );
}
